import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ChevronLeft, ChevronRight } from "lucide-react";
import HomeHeader from "./details-header/details-header";
import HomeFooter from "../homepage/home-footer/home-footer";
import "./counselor-details.css";
import Loading from "../loading/Loading";

const CounselorDetails = () => {
  const { counselor_id } = useParams();
  const navigate = useNavigate();
  const [currentCounselor, setCurrentCounselor] = useState(null);
  const [allCounselors, setAllCounselors] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const COUNSELOR_API_ENDPOINT = "/wp-json/dbsb/v1/counselors";
  const ALL_COUNSELORS_API_ENDPOINT = "/wp-json/dbsb/v1/counselors";

  // Fetch all counselors for navigation
  useEffect(() => {
    const fetchAllCounselors = async () => {
      try {
        const response = await fetch(`${ALL_COUNSELORS_API_ENDPOINT}?per_page=50&page=1`, {
          method: "GET",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
          },
        });

        if (response.ok) {
          const data = await response.json();
          const mappedCounselors = data.counselors.map((counselor) => ({
            id: counselor.slug || counselor.id.toString(),
            name: counselor.name,
            role: counselor.title || "Genetic Counselor",
            image: counselor.featured_image?.url || counselor.avatar_url || `${process.env.PUBLIC_URL}/assets/counselor-svg.svg`,
            bio: counselor.biography || "",
            specialization: counselor.specialization || "",
            experience_years: counselor.experience_years || "",
            email: counselor.email || "",
            phone: counselor.phone || "",
          }));
          setAllCounselors(mappedCounselors);
        }
      } catch (error) {
        console.error("Error fetching all counselors:", error);
      }
    };

    fetchAllCounselors();
  }, []);

  // Fetch specific counselor details
  useEffect(() => {
    const fetchCounselorDetails = async () => {
      if (!counselor_id) {
        setError("No counselor ID provided.");
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      setError(null);

      try {
        // Try to fetch by slug first, then by ID
        let response = await fetch(`${COUNSELOR_API_ENDPOINT}/slug/${counselor_id}`, {
          method: "GET",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
          },
        });

        if (!response.ok) {
          // If slug doesn't work, try by ID
          response = await fetch(`${COUNSELOR_API_ENDPOINT}/${counselor_id}`, {
            method: "GET",
            credentials: "include",
            headers: {
              "Content-Type": "application/json",
            },
          });
        }

        if (!response.ok) {
          if (response.status === 404) {
            throw new Error("Counselor not found.");
          }
          throw new Error("Failed to fetch counselor details.");
        }

        const data = await response.json();
        const mappedCounselor = {
          id: data.slug || data.id.toString(),
          name: data.name,
          role: data.title || "Genetic Counselor",
          image: data.featured_image?.url || data.avatar_url || `${process.env.PUBLIC_URL}/assets/counselor-svg.svg`,
          bio: data.biography || "",
          specialization: data.specialization || "",
          experience_years: data.experience_years || "",
          email: data.email || "",
          phone: data.phone || "",
        };

        setCurrentCounselor(mappedCounselor);
      } catch (error) {
        console.error("Error fetching counselor details:", error);
        setError(error.message);
        setCurrentCounselor(null); // Don't set fallback data
      } finally {
        setIsLoading(false);
      }
    };

    fetchCounselorDetails();
  }, [counselor_id]);

  // Scroll to top when the component loads or the counselor ID changes
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [counselor_id]);

  // Find current counselor index for navigation
  const currentIndex = allCounselors.findIndex((c) => c.id === counselor_id);
  const nextCounselorIndex = (currentIndex + 1) % allCounselors.length;
  const nextCounselorId = allCounselors[nextCounselorIndex]?.id;

  if (isLoading) {
    return (
      <motion.div
        className="counselor-details-page"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5 }}
      >
        <HomeHeader />
        <main className="cd-main-content">
          <div className="cd-container">
            <div className="loading-container">
              <Loading />
            </div>
          </div>
        </main>
        <HomeFooter />
      </motion.div>
    );
  }

  if (!currentCounselor) {
    return (
      <motion.div
        className="counselor-details-page"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5 }}
      >
        <HomeHeader />
        <main className="cd-main-content">
          <div className="cd-container">
            <motion.button
              onClick={() => navigate(-1)}
              className="cd-back-button"
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 }}
            >
              <ChevronLeft size={27} />
              Back
            </motion.button>
            <div className="error-container">
              <p>Counselor not found.</p>
            </div>
          </div>
        </main>
        <HomeFooter />
      </motion.div>
    );
  }
  
  return (
    <motion.div
      className="counselor-details-page"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      <HomeHeader />
      <main className="cd-main-content">
        <div className="cd-container">
          <motion.button
            onClick={() => navigate(-1)}
            className="cd-back-button"
            initial={{ opacity: 0, x: -10 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2 }}
          >
            <ChevronLeft size={27} />
            Back
          </motion.button>

          <motion.div
            className="cd-content-grid"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            {/* Left Column */}
            <div className="cd-left-column">
              <h1 className="cd-counselor-name">{currentCounselor.name}</h1>
              <p className="cd-counselor-role">{currentCounselor.role}</p>
            </div>

            <div className="cd-counselor-image-bio">
              <div className="cd-image-wrapper">
                <img
                  src={currentCounselor.image}
                  alt={`Portrait of ${currentCounselor.name}`}
                  className="cd-counselor-image"
                  onError={(e) => {
                    e.target.src = `${process.env.PUBLIC_URL}/assets/counselor-svg.svg`;
                  }}
                />
              </div>

            {/* Right Column */}
            <div className="cd-right-column">
              <div className="cd-counselor-bio">
                <p>{currentCounselor.bio}</p>
              </div>
            </div>
            </div>
          </motion.div>
          
          {allCounselors.length > 1 && (
            <motion.div
              className="cd-navigation"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3 }}
            >
              <button
                onClick={() => navigate(`/counselor-details/${nextCounselorId}`)}
                className="cd-next-button"
              >
                Next Team Member
                <ChevronRight size={27} />
              </button>
            </motion.div>
          )}
        </div>
      </main>
      <HomeFooter />
    </motion.div>
  );
};

export default CounselorDetails;